import { Box } from '@mui/material'
import { createFileRoute } from '@tanstack/react-router'
import { DotStream } from 'ldrs/react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { CanteenDashboardLayout } from '@/app/layouts/canteen-dashboard-layout'
import { hasAnyRole, ROLE_GROUPS } from '@/shared/const'
import { AbilityProvider } from '@/shared/permissions'
import { Select } from '@/shared/ui/multiple-select/ui/MultipleSelect'
import { NotFound } from '@/shared/ui/not-found'
import { DashboardWidgetView, useDashboardControls } from '@/widgets/dashboard-widget'

const LOADER_COLOR = '#000000'

export const Route = createFileRoute('/canteen-dashboard')({
  component: CanteenDashboardPage,
})

function CanteenDashboardPage() {
  const { t } = useTranslation()
  const controls = useDashboardControls({ lockDateToToday: true })

  const roles = controls.currentUser?.roles ?? []

  const hasAccess = useMemo(() => {
    return hasAnyRole(roles, ROLE_GROUPS.CANTEEN_DASHBOARD_ACCESS)
  }, [roles])

  if (!controls.currentUser) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <DotStream size={64} speed={1.5} color={LOADER_COLOR} />
      </Box>
    )
  }

  if (!hasAccess) {
    return <NotFound />
  }

  const selectedSchoolOption = controls.schoolOptions.find(
    (option) => option.value === controls.selectedSchoolId
  )

  const schoolSelect = (
    <Select
      options={controls.schoolOptions}
      select={controls.selectedSchoolId ?? ''}
      onChange={(value: string | number | (string | number)[] | undefined) =>
        controls.onSchoolChange(value ? String(value) : undefined)
      }
      multiply={false}
      showSearch={!controls.isAdminSchool}
      onSearch={controls.onSchoolSearch}
      hasNextPage={controls.hasNextSchoolPage}
      fetchNextPage={controls.fetchNextSchoolPage}
      isFetchingNextPage={controls.isFetchingNextSchoolPage}
      isLoading={controls.isLoadingSchools}
      placeholder={t('Выберите школу')}
      showResetBtn={false}
      width="100%"
      disabled={controls.isAdminSchool}
    />
  )

  const schoolName =
    selectedSchoolOption?.title ??
    controls.currentUser.company?.nameRu ??
    controls.currentUser.company?.nameKk ??
    ''
  const canteenName = controls.currentUser.company?.nameRu

  const contentReady = Boolean(controls.selectedSchoolIdForRequest)

  return (
    <AbilityProvider roles={roles}>
      <CanteenDashboardLayout
        schoolName={schoolName}
        canteenName={canteenName}
        schoolSelect={schoolSelect}
      >
        {contentReady ? (
          <DashboardWidgetView controls={controls} showFilters={false} cardLayout="canteen" />
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 'calc(100vh - 120px)',
            }}
          >
            <DotStream size={64} speed={1.5} color={LOADER_COLOR} />
          </Box>
        )}
      </CanteenDashboardLayout>
    </AbilityProvider>
  )
}
